<template>
<div class="pl-jp">
    <div class="pl-jp-hd">
        <h2>
            <svg class="pl-i"><use href="#pl-tray"/></svg>
            Q{{ quoteId }} — where it's up to
        </h2>
        <span v-if="detail" class="pl-jp-counts">
            <b>{{ counts.done }}</b> of {{ counts.total }} done
            <template v-if="counts.scheduled"> · {{ counts.scheduled }} scheduled</template>
            <template v-if="counts.unscheduled"> · <span class="pl-jp-warn">{{ counts.unscheduled }} unscheduled</span></template>
        </span>
        <!-- Single-item job: the one sheet link lives up here. Multi-item
             jobs get a link per item header instead, beside its processes. -->
        <a v-if="detail && items.length === 1"
           class="pl-jp-sheet"
           :href="sheetUrl(items[0].id)"
           :title="'Open the production sheet — ' + labelFull(items[0])"
           data-ajax="false" target="_blank" rel="noopener">
            📄 {{ label(items[0], 0, 1) }}
        </a>
        <button v-if="canAssign" type="button" class="pl-jp-assign" @click="$emit('assign')">
            Assign the rest →
        </button>
        <!-- Auto-planned drafts (qapp:planner-morning / Gate 2 override)
             sit dashed until a replanner accepts them; staff dashboards
             hide drafts, so this is what puts the plan in front of them. -->
        <button v-if="draftCount && permissions.can_replan" type="button" class="pl-jp-assign"
                :disabled="confirming" @click="confirmDrafts"
                :title="'Accept the ' + draftCount + ' draft slot' + (draftCount === 1 ? '' : 's') + ' on this job so they show on staff dashboards'">
            {{ confirming ? 'Confirming…' : '✓ Confirm ' + draftCount + ' draft' + (draftCount === 1 ? '' : 's') }}
        </button>
        <button v-if="detail && !detail.removed && permissions.can_replan" type="button" class="pl-jp-remove"
                @click="removing = !removing"
                title="Client cancelled or job on hold: take every unfinished slot off people's days and keep it out of auto-plan">
            ✕ Remove from planner
        </button>
        <button type="button" class="pl-jp-fold" @click="open = !open">{{ open ? '▾ Collapse' : '▸ Expand' }}</button>

        <!-- P4 job flags: the scribbles on the magnet. Pills for everyone;
             the artwork control for the artwork lead + planning roles, the
             ★ for replanners. Flags only — nothing here releases or ticks.
             Hidden when the job isn't on the board's jobs list (finished,
             or not yet in flight). -->
        <div v-if="jobFlags" class="pl-jp-flags">
            <flag-pills :flags="jobFlags" :show-in="true" :show-pre="true" />

            <span v-if="permissions.can_set_artwork" class="pl-jp-seg" role="group" aria-label="Artwork status">
                <span class="pl-jp-seg-lbl">Artwork:</span>
                <button v-for="opt in artworkOptions" :key="opt.value" type="button"
                        class="pl-jp-seg-btn" :class="{ on: jobFlags.artwork === opt.value }"
                        :disabled="savingFlag !== null"
                        :title="jobFlags.artwork === opt.value ? 'Click again to clear' : opt.title"
                        @click="setArtwork(opt.value)">{{ opt.label }}</button>
            </span>
            <span v-if="permissions.can_set_artwork && jobFlags.artwork && jobFlags.artwork_by" class="pl-jp-flag-by">
                set by {{ jobFlags.artwork_by }}<template v-if="jobFlags.artwork_at"> · {{ fmtDay(jobFlags.artwork_at.slice(0, 10)) }}</template>
            </span>

            <button v-if="permissions.can_set_priority" type="button"
                    class="pl-jp-pri" :class="{ on: jobFlags.priority }"
                    :disabled="savingFlag !== null"
                    :title="jobFlags.priority ? 'Priority — click to clear the star' : 'Star this job as a priority'"
                    @click="togglePriority">★ Priority</button>
        </div>
    </div>

    <!-- Remove job: a reason is required, and the form says exactly what
         goes (unfinished slots) and what doesn't (done work, the quote). -->
    <div v-if="removing && detail && !detail.removed" class="pl-jp-rm">
        <div class="pl-jp-rm-t">Remove Q{{ quoteId }} from the planner?</div>
        <p class="pl-jp-rm-p">
            Takes <b>{{ removeCounts.slots }} unfinished slot{{ removeCounts.slots === 1 ? '' : 's' }}</b> off people's days
            and keeps the job out of the tray and auto-plan.
            <template v-if="removeCounts.done">{{ removeCounts.done }} done process{{ removeCounts.done === 1 ? '' : 'es' }} stay as history.</template>
            <template v-if="removeCounts.inProgress.length"><br><span class="pl-jp-warn">In progress now: {{ removeCounts.inProgress.join(', ') }} — let them know.</span></template>
            <br>The quote, its invoice and the HubSpot deal are <b>not</b> changed. If the client has cancelled, mark the quote lost too.
        </p>
        <div class="pl-jp-rm-row">
            <button v-for="r in removeReasons" :key="r" type="button" class="pl-jp-rm-chip"
                    :class="{ on: removeReason === r }" @click="removeReason = r">{{ r }}</button>
            <input v-model="removeReason" class="pl-jp-rm-in" maxlength="255" placeholder="or type the reason…">
        </div>
        <div class="pl-jp-rm-row">
            <button type="button" class="pl-jp-rm-go" :disabled="removeBusy || !removeReason.trim()" @click="removeJob">
                {{ removeBusy ? 'Removing…' : 'Remove from planner' }}
            </button>
            <button type="button" class="pl-jp-fold" :disabled="removeBusy" @click="removing = false">Cancel</button>
        </div>
    </div>

    <div v-if="detail && detail.removed" class="pl-jp-gone">
        <span>
            <b>Removed from the planner</b>
            {{ fmtDay(detail.removed.at.slice(0, 10)) }}<template v-if="detail.removed.by"> by {{ detail.removed.by }}</template>
            <template v-if="detail.removed.reason"> — “{{ detail.removed.reason }}”</template>
        </span>
        <button v-if="permissions.can_replan" type="button" class="pl-jp-assign" :disabled="removeBusy" @click="restoreJob"
                title="Put the job back: slots from today on are reinstated, the rest go to the unscheduled tray">
            {{ removeBusy ? 'Restoring…' : '↩ Restore to planner' }}
        </button>
    </div>

    <div v-show="open" class="pl-jp-body" :class="{ 'pl-jp-body-full': fullHeight }">
        <div v-if="loading && !detail" class="pl-jp-note">Loading Q{{ quoteId }}…</div>
        <div v-else-if="detail && !detail.processes.length" class="pl-jp-note">
            No production processes on this job yet — they appear once it's released to production.
        </div>

        <template v-else-if="detail">
            <div v-for="entry in renderList" :key="entry.key">

                <!-- item header: which item the processes below belong to,
                     with its production sheet (dims, scribbles, measured-by) -->
                <div v-if="entry.kind === 'item'" class="pl-jp-item">
                    <span class="pl-jp-item-nm" :title="labelFull(entry.bucket.item)">
                        {{ label(entry.bucket.item, entry.bucket.index, items.length) }}
                    </span>
                    <span class="pl-jp-item-ct">{{ entry.bucket.rows.length }} process<template v-if="entry.bucket.rows.length !== 1">es</template></span>
                    <a v-if="entry.bucket.item"
                       class="pl-jp-sheet"
                       :href="sheetUrl(entry.bucket.item.id)"
                       :title="'Open the production sheet — ' + labelFull(entry.bucket.item)"
                       data-ajax="false" target="_blank" rel="noopener">
                        📄 Production sheet
                    </a>
                </div>

                <!-- collapsed group of same-name processes (mega-jobs) -->
                <button v-if="entry.kind === 'group'" type="button" class="pl-jp-row pl-jp-grp"
                        :class="{ 'pl-jp-under': multiItem }"
                        :style="{ '--plwc': wc.colour(entry.group.workcentre_id) }"
                        @click="toggleGroup(entry.group.groupKey)">
                    <svg class="pl-i"><use :href="'#' + wc.icon(entry.group.workcentre_id)"/></svg>
                    <span class="pl-jp-nm">{{ entry.group.pretty_name }} <small>× {{ entry.group.rows.length }}</small></span>
                    <span class="pl-jp-status">
                        <span class="pl-jp-pill" :class="entry.group.done === entry.group.rows.length ? 'pl-jp-pill-done' : ''">
                            {{ entry.group.done }}/{{ entry.group.rows.length }} ✓
                        </span>
                        <template v-if="entry.group.firstDate"> {{ fmtDay(entry.group.firstDate) }}<template v-if="entry.group.lastDate !== entry.group.firstDate"> – {{ fmtDay(entry.group.lastDate) }}</template></template>
                        <template v-if="entry.group.people.length"> · {{ entry.group.people.join(', ') }}</template>
                    </span>
                    <span class="pl-jp-est">{{ fmtMins(entry.group.estMinutes) }}</span>
                    <span class="pl-jp-chev">{{ openGroups[entry.group.groupKey] ? '▾' : '▸' }}</span>
                </button>

                <!-- one process -->
                <div v-else-if="entry.kind === 'row'" class="pl-jp-row"
                     :class="{ 'pl-jp-done': entry.row.checked, 'pl-jp-ing': entry.inGroup, 'pl-jp-under': multiItem }"
                     :style="{ '--plwc': wc.colour(entry.row.workcentre_id) }">
                    <!-- checklist-view tick: per-PROCESS only, through the same
                         PUT /checklist the drawer and My Day use — never an
                         item-level tick (the old page's corruption vector) -->
                    <input v-if="tickable && permissions.can_tick" type="checkbox" class="pl-jp-cb"
                           :checked="entry.row.checked"
                           :disabled="ticking === entry.row.checklist_id"
                           :title="entry.row.checked ? 'Untick — marks this process not done' : 'Mark this process done'"
                           @change="tickRow(entry.row, $event)">
                    <svg class="pl-i"><use :href="'#' + wc.icon(entry.row.workcentre_id)"/></svg>
                    <span class="pl-jp-nm">{{ entry.row.pretty_name }}</span>

                    <span class="pl-jp-status">
                        <!-- placement: one chip per slot, click = same drawer as the calendar card -->
                        <button v-for="slot in entry.row.slots" :key="'sl' + slot.id" type="button"
                                class="pl-jp-slot" :class="{ 'pl-jp-slot-done': slot.completed_at }"
                                :title="'Open — same card as on the calendar'"
                                @click="$emit('open-chip', makeChip(entry.row, slot))">
                            <template v-if="slot.completed_at">✓ </template>{{ fmtDay(slot.date) }} — {{ slot.user_name || '?' }}
                            <i v-if="slot.is_draft" class="pl-jp-draft">draft</i>
                        </button>

                        <span v-if="!entry.row.slots.length && !entry.row.checked" class="pl-jp-pill pl-jp-pill-un">unscheduled</span>

                        <!-- multi-person progress: 1/2 ✓ while partially ticked -->
                        <span v-if="entry.row.num_people > 1 && !entry.row.checked && entry.row.ticked_by.length"
                              class="pl-jp-pill pl-jp-pill-part"
                              :title="'Ticked so far: ' + entry.row.ticked_by.map(t => t.name || '?').join(', ')">
                            {{ entry.row.ticked_by.length }}/{{ entry.row.num_people }} ✓
                        </span>

                        <template v-if="entry.row.checked">
                            <button v-if="!entry.row.slots.length" type="button" class="pl-jp-pill pl-jp-pill-done pl-jp-tickbtn"
                                    title="Open — undo the tick from here if it's wrong"
                                    @click="$emit('open-chip', makeChip(entry.row, null))">
                                ✓ done{{ tickLabel(entry.row) }}
                            </button>
                            <span v-else class="pl-jp-pill pl-jp-pill-done">✓ done{{ tickLabel(entry.row) }}</span>
                        </template>
                    </span>

                    <span class="pl-jp-est">{{ fmtMins(entry.row.est_minutes) }}<template v-if="entry.row.num_people > 1"> · {{ entry.row.num_people }}p</template></span>
                </div>
            </div>
        </template>
    </div>
</div>
</template>

<script>
import FlagPills from './FlagPills';
import { fmtMins, errMsg, wcLookups, productionSheetUrl, itemLabel, itemLabelFull } from './shared';

// Above this many rows the flat list stops being readable (trickle
// mega-orders run to hundreds) — collapse to one row per process name.
const GROUP_THRESHOLD = 25;

// The artwork segmented control (P4). Values are the quotes.artwork_status
// column; clicking the active one clears back to null ("not set").
const ARTWORK_OPTIONS = [
    { value: 'none',    label: 'None needed', title: 'No artwork needed for this job' },
    { value: 'waiting', label: '⏳ Waiting',   title: 'Waiting on artwork from the client' },
    { value: 'in',      label: '✓ In',        title: 'Artwork is in — production can go ahead' },
];

// One-click reasons for taking a job off the planner; free text otherwise.
const REMOVE_REASONS = ['Client cancelled', 'On hold', 'Duplicate job'];

export default {
    name: 'JobProcessPanel',
    components: { FlagPills },

    props: {
        quoteId: { type: Number, required: true },
        board: { type: Object, required: true },
        permissions: { type: Object, required: true },
        // parent bumps this after every board fetch so the panel stays
        // in step with drawer ticks / drags / Echo refreshes
        refreshKey: { type: Number, default: 0 },
        // Checklist view only: render a per-process tick checkbox. The
        // search-strip instance leaves this off — there, ticking goes
        // through the ChipDrawer (Peter's 2026-07-27 choice stands).
        tickable: { type: Boolean, default: false },
        // Checklist view only: no inner scroll — an expanded job shows every
        // process at full height (Peter, 2026-08-17). The search strip keeps
        // its 300px cap so the panel can't push the board off-screen.
        fullHeight: { type: Boolean, default: false },
    },

    data() {
        return {
            detail: null,
            loading: false,
            open: true,
            openGroups: {},
            ticking: null,   // checklist_id mid-save
            savingFlag: null,   // 'artwork' | 'priority' while a flag POST is in flight
            confirming: false,   // confirm-drafts POST in flight
            removing: false,     // remove-job confirm form open
            removeReason: '',
            removeBusy: false,
        };
    },

    created() {
        this.fetch();
    },

    watch: {
        quoteId() {
            this.detail = null;
            this.removing = false;
            this.removeReason = '';
            this.openGroups = {};
            this.fetch();
        },
        refreshKey() {
            this.fetch();
        },
    },

    computed: {
        wc() {
            return wcLookups(this.board.workcentres);
        },

        rows() {
            if (!this.detail) return [];
            // Reading order = production order: done history first, then
            // scheduled work by date, unscheduled last.
            return this.detail.processes.slice().sort((a, b) => {
                const ka = this.sortKey(a), kb = this.sortKey(b);
                return ka < kb ? -1 : ka > kb ? 1 : 0;
            });
        },

        counts() {
            const total = this.rows.length;
            const done = this.rows.filter(r => r.checked).length;
            const scheduled = this.rows.filter(r => !r.checked && r.slots.length).length;
            return { total, done, scheduled, unscheduled: total - done - scheduled };
        },

        items() {
            return (this.detail && this.detail.items) || [];
        },

        multiItem() {
            return this.items.length > 1;
        },

        /* Processes split by the item they belong to, in quote order. A
           trailing null-item bucket catches rows with no item_id (legacy
           checklist rows, item deleted) so nothing goes missing. */
        buckets() {
            if (!this.multiItem) {
                return [{ item: this.items[0] || null, index: 0, rows: this.rows }];
            }
            const byItem = {};
            this.items.forEach((it, i) => {
                byItem[it.id] = { item: it, index: i, rows: [] };
            });
            const orphans = { item: null, index: null, rows: [] };
            this.rows.forEach(r => {
                (byItem[r.item_id] || orphans).rows.push(r);
            });
            const out = this.items.map(it => byItem[it.id]).filter(b => b.rows.length);
            if (orphans.rows.length) out.push(orphans);
            return out;
        },

        groupsFor() {
            // Grouping kicks in on the whole job, but keys include the item
            // so a mega-order's "Welding" rows don't merge across items.
            return rows => {
                const byKey = {};
                const order = [];
                rows.forEach(r => {
                    const key = r.item_id + '|' + r.pretty_name + '|' + (r.workcentre_id || 0);
                    if (!byKey[key]) {
                        byKey[key] = {
                            groupKey: key,
                            pretty_name: r.pretty_name,
                            workcentre_id: r.workcentre_id,
                            rows: [], done: 0, estMinutes: 0,
                            firstDate: null, lastDate: null, people: [],
                        };
                        order.push(key);
                    }
                    const g = byKey[key];
                    g.rows.push(r);
                    if (r.checked) g.done++;
                    g.estMinutes += r.est_minutes;
                    r.slots.forEach(s => {
                        if (!g.firstDate || s.date < g.firstDate) g.firstDate = s.date;
                        if (!g.lastDate || s.date > g.lastDate) g.lastDate = s.date;
                        if (s.user_name && g.people.indexOf(s.user_name) === -1) g.people.push(s.user_name);
                    });
                });
                return order.map(k => byKey[k]);
            };
        },

        renderList() {
            const grouped = this.rows.length > GROUP_THRESHOLD;
            const out = [];

            this.buckets.forEach(b => {
                if (this.multiItem) {
                    out.push({ kind: 'item', bucket: b, key: 'i' + (b.item ? b.item.id : 'none') });
                }
                if (grouped) {
                    this.groupsFor(b.rows).forEach(g => {
                        out.push({ kind: 'group', group: g, key: 'g' + g.groupKey });
                        if (this.openGroups[g.groupKey]) {
                            g.rows.forEach(r => out.push({ kind: 'row', row: r, inGroup: true, key: 'r' + r.checklist_id }));
                        }
                    });
                } else {
                    b.rows.forEach(r => out.push({ kind: 'row', row: r, key: 'r' + r.checklist_id }));
                }
            });

            return out;
        },

        draftCount() {
            return this.rows.reduce((n, r) => n + (r.checked ? 0 : r.slots.filter(sl => sl.is_draft).length), 0);
        },

        canAssign() {
            return this.counts.unscheduled > 0
                && !(this.detail && this.detail.removed)   // restore first, then plan
                && (this.permissions.can_place || this.permissions.can_replan);
        },

        /* The job's flags, read off board.jobs so this panel can never
           disagree with the lane row, the chips or the checklist. Null
           when the job isn't on the board — the flags block hides. */
        jobFlags() {
            const jobs = this.board.jobs || [];
            const job = jobs.find(j => j.quote_id === this.quoteId);
            return (job && job.flags) || null;
        },

        artworkOptions() {
            return ARTWORK_OPTIONS;
        },

        removeReasons() {
            return REMOVE_REASONS;
        },

        /* What a removal takes: every unfinished slot (the server deletes
           exactly these), and who is part-way through one right now. */
        removeCounts() {
            let slots = 0;
            const inProgress = [];
            this.rows.forEach(r => r.slots.forEach(sl => {
                if (sl.completed_at) return;
                slots++;
                if (sl.started_at && sl.user_name && inProgress.indexOf(sl.user_name) === -1) inProgress.push(sl.user_name);
            }));
            return { slots, done: this.counts.done, inProgress };
        },
    },

    methods: {
        fmtMins,
        sheetUrl: productionSheetUrl,
        label: itemLabel,
        labelFull: itemLabelFull,

        confirmDrafts() {
            this.confirming = true;
            window.axios.post('/planner/quote/' + this.quoteId + '/confirm-drafts')
                .then(() => {
                    this.fetch();
                    this.$emit('changed');   // parent refetches the board
                })
                .catch(err => { this.$emit('error', errMsg(err)); })
                .then(() => { this.confirming = false; });
        },

        removeJob() {
            if (this.removeBusy || !this.removeReason.trim()) return;
            this.removeBusy = true;
            const reason = this.removeReason.trim();
            window.axios.post('/planner/quote/' + this.quoteId + '/remove', { reason })
                .then(res => {
                    this.removing = false;
                    this.removeReason = '';
                    // the parent shows the receipt (with Undo) and refetches
                    this.$emit('removed', { quoteId: this.quoteId, result: res.data });
                    this.$emit('changed');
                    this.fetch();
                })
                .catch(err => { this.$emit('error', errMsg(err)); })
                .then(() => { this.removeBusy = false; });
        },

        restoreJob() {
            if (this.removeBusy) return;
            this.removeBusy = true;
            window.axios.post('/planner/quote/' + this.quoteId + '/restore', { mode: 'future' })
                .then(res => {
                    this.$emit('restored', { quoteId: this.quoteId, result: res.data });
                    this.$emit('changed');
                    this.fetch();
                })
                .catch(err => { this.$emit('error', errMsg(err)); })
                .then(() => { this.removeBusy = false; });
        },

        fetch() {
            if (!this.quoteId) return;
            this.loading = true;
            window.axios.get('/planner/quote/' + this.quoteId + '/processes')
                .then(res => { this.detail = res.data; })
                .catch(err => { this.$emit('error', errMsg(err)); })
                .then(() => { this.loading = false; });
        },

        /* Checklist-view tick. Same endpoint + payload as the drawer and
           the staff dashboard, so the Block 3 all-ticked trigger, slot
           completed_at stamping and multi-person tick handling inside
           checkedState() all apply identically. On a multi-person row a
           tick records THIS user's tick; the box only shows checked once
           everyone has ticked (the n/m pill covers the in-between). */
        tickRow(row, ev) {
            const checked = ev.target.checked;
            this.ticking = row.checklist_id;
            window.axios.put('/checklist', { type: 'process', id: row.checklist_id, checked })
                .then(() => {
                    this.fetch();
                    this.$emit('changed');   // parent refetches the board (job % bars)
                })
                .catch(err => {
                    ev.target.checked = !checked;   // snap back — the save didn't happen
                    this.$emit('error', errMsg(err));
                })
                .then(() => { this.ticking = null; });
        },

        /* ---- P4 flags: each click POSTs only the key it changes ---- */
        setArtwork(value) {
            const current = this.jobFlags ? this.jobFlags.artwork : null;
            // clicking the active segment clears the status back to "not set"
            this.saveFlags({ artwork_status: current === value ? null : value }, 'artwork');
        },

        togglePriority() {
            const current = !!(this.jobFlags && this.jobFlags.priority);
            this.saveFlags({ is_priority: !current }, 'priority');
        },

        saveFlags(payload, key) {
            if (this.savingFlag) return;
            this.savingFlag = key;
            window.axios.post('/planner/quote/' + this.quoteId + '/flags', payload)
                .then(() => {
                    // the parent refetches the board, which is where
                    // jobFlags reads from — every surface updates at once
                    this.$emit('changed');
                })
                .catch(err => { this.$emit('error', errMsg(err)); })
                .then(() => { this.savingFlag = null; });
        },

        sortKey(row) {
            if (row.slots.length) return row.slots[0].date;
            return row.checked ? '0000' : '9999';
        },

        toggleGroup(key) {
            this.$set(this.openGroups, key, !this.openGroups[key]);
        },

        fmtDay(date) {
            return new Date(date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
        },

        tickLabel(row) {
            if (!row.ticked_by.length) return '';
            const first = row.ticked_by[0];
            const extra = row.ticked_by.length - 1;
            let label = ' — ' + (first.name || '?');
            if (extra > 0) label += ' +' + extra;
            if (first.at) label += ', ' + this.fmtDay(first.at.substr(0, 10));
            return label;
        },

        /* Same shape as a board slot row, so ChipDrawer behaves exactly
           as it does from a calendar card. Slot-less ticked rows get a
           null-id chip: every slot write in the drawer is behind
           `movable`, which is always false once checked. */
        makeChip(row, slot) {
            return {
                id: slot ? slot.id : null,
                checklist_id: row.checklist_id,
                type: 'process',
                title: null,
                user_id: slot ? slot.user_id : null,
                date: slot ? slot.date : null,
                pos: slot ? slot.pos : 0,
                planned_seconds: slot ? slot.planned_seconds : row.est_minutes * 60,
                is_draft: slot ? slot.is_draft : false,
                started_at: slot ? slot.started_at : null,
                completed_at: slot ? slot.completed_at : null,
                process_name: row.process_name,
                pretty_name: row.pretty_name,
                checked: row.checked,
                released: row.released,
                item_id: row.item_id,
                num_people: row.num_people,
                workcentre_id: row.workcentre_id,
                quote_id: this.detail.quote_id,
                quote_name: this.detail.quote_name,
            };
        },
    },
};
</script>

<style scoped>
.pl-jp{background:var(--pl-card); border:1px solid var(--pl-line); border-radius:14px;
    box-shadow:0 1px 2px rgba(11,11,11,.06); margin-bottom:14px; overflow:hidden}
.pl-jp-hd{display:flex; align-items:center; gap:12px; flex-wrap:wrap; padding:10px 16px}
.pl-jp-hd h2{display:flex; align-items:center; gap:8px; font-size:14px; font-weight:800; margin:0}
.pl-jp-hd h2 .pl-i{width:15px; height:15px}
.pl-jp-counts{font-size:13px; color:var(--pl-ink2); font-weight:600}
.pl-jp-warn{color:var(--pl-warn); font-weight:800}
.pl-jp-assign{border:1px solid var(--pl-ink); background:var(--pl-ink); color:#fff;
    border-radius:8px; padding:6px 12px; font-weight:700; font-size:12.5px}
.pl-jp-remove{border:1px solid var(--pl-crit); background:transparent; color:var(--pl-crit);
    border-radius:8px; padding:6px 12px; font-weight:700; font-size:12.5px}
.pl-jp-remove:hover{background:var(--pl-crit-soft)}
.pl-jp-rm{margin:0 16px 12px; padding:12px 14px; border:1.5px solid var(--pl-crit); border-radius:10px; background:var(--pl-crit-soft)}
.pl-jp-rm-t{font-weight:800; font-size:13.5px; margin-bottom:4px}
.pl-jp-rm-p{font-size:12.5px; color:var(--pl-ink); margin:0 0 8px; line-height:1.5}
.pl-jp-rm-row{display:flex; gap:8px; flex-wrap:wrap; align-items:center; margin-top:6px}
.pl-jp-rm-chip{border:1px solid var(--pl-line); background:var(--pl-card); border-radius:20px; padding:4px 11px; font-size:12px; font-weight:700}
.pl-jp-rm-chip.on{background:var(--pl-ink); border-color:var(--pl-ink); color:#fff}
.pl-jp-rm-in{flex:1 1 180px; min-width:0; border:1px solid var(--pl-line); border-radius:8px; padding:5px 9px; font-size:12.5px}
.pl-jp-rm-go{border:1px solid var(--pl-crit); background:var(--pl-crit); color:#fff; border-radius:8px; padding:6px 14px; font-weight:800; font-size:12.5px}
.pl-jp-rm-go:disabled{opacity:.5}
.pl-jp-gone{display:flex; align-items:center; gap:12px; flex-wrap:wrap; margin:0 16px 12px; padding:9px 12px;
    border-radius:10px; background:var(--pl-crit-soft); font-size:12.5px}
.pl-jp-fold{margin-left:auto; border:0; background:transparent; color:var(--pl-ink2); font-weight:700; font-size:12.5px}

/* P4 flags row — its own line under the title so the header stays readable */
.pl-jp-flags{flex-basis:100%; display:flex; align-items:center; gap:10px; flex-wrap:wrap;
    padding-top:6px; border-top:1px dashed var(--pl-line)}
.pl-jp-seg{display:inline-flex; align-items:center; gap:0; border:1px solid var(--pl-line);
    border-radius:20px; overflow:hidden; background:var(--pl-card2)}
.pl-jp-seg-lbl{font-size:11.5px; font-weight:700; color:var(--pl-ink2); padding:0 8px 0 10px}
.pl-jp-seg-btn{border:0; border-left:1px solid var(--pl-line); background:transparent; color:var(--pl-ink2);
    padding:4px 10px; font-size:12px; font-weight:700; white-space:nowrap; cursor:pointer}
.pl-jp-seg-btn:hover{color:var(--pl-ink); background:var(--pl-card)}
.pl-jp-seg-btn.on{background:var(--pl-ink); color:#fff}
.pl-jp-seg-btn:disabled{cursor:wait; opacity:.6}
.pl-jp-flag-by{font-size:11.5px; color:var(--pl-ink2); font-weight:600}
.pl-jp-pri{border:1px solid var(--pl-line); background:var(--pl-card2); color:var(--pl-ink2);
    border-radius:20px; padding:4px 11px; font-size:12px; font-weight:800; white-space:nowrap; cursor:pointer}
.pl-jp-pri:hover{color:var(--pl-ink); border-color:var(--pl-ink3)}
.pl-jp-pri.on{background:#fdf3d9; border-color:#e6c46a; color:#8a5a00}
.pl-jp-pri:disabled{cursor:wait; opacity:.6}

/* Item tier: which item these processes belong to + its scribble sheet */
.pl-jp-item{display:flex; align-items:center; gap:9px; flex-wrap:wrap;
    padding:9px 8px 5px 4px; margin-top:2px; border-top:1px solid var(--pl-line)}
.pl-jp-item:first-child{border-top:0; margin-top:0}
.pl-jp-item-nm{font-weight:800; font-size:13px; color:var(--pl-ink)}
.pl-jp-item-ct{font-size:11.5px; color:var(--pl-ink2); font-weight:650}
.pl-jp-sheet{margin-left:auto; flex:none; border:1px solid var(--pl-line); background:var(--pl-card2);
    border-radius:20px; padding:3px 12px; font-size:12px; font-weight:700;
    color:var(--pl-ink); text-decoration:none; white-space:nowrap}
.pl-jp-sheet:hover,.pl-jp-sheet:focus{border-color:var(--pl-ink3); color:var(--pl-ink); text-decoration:none}
/* processes read as belonging to the item header above them */
.pl-jp-under{margin-left:10px}
.pl-jp-under.pl-jp-ing{margin-left:32px}
.pl-jp-body{border-top:1px solid var(--pl-line); max-height:300px; overflow-y:auto; padding:6px 10px}
.pl-jp-body-full{max-height:none; overflow-y:visible}
.pl-jp-note{font-size:13px; color:var(--pl-ink2); padding:8px 6px}

.pl-jp-row{display:flex; align-items:center; gap:9px; width:100%; text-align:left;
    padding:6px 8px 6px 10px; border:0; background:transparent; border-radius:8px; position:relative}
.pl-jp-row::before{content:""; position:absolute; left:0; top:6px; bottom:6px; width:4px;
    border-radius:4px; background:var(--plwc, #898781)}
.pl-jp-row:nth-child(odd){background:transparent}
.pl-jp-row .pl-i{width:14px; height:14px; flex:none; color:var(--plwc, #898781)}
.pl-jp-nm{font-weight:700; font-size:13.5px; flex:none; min-width:150px}
.pl-jp-nm small{color:var(--pl-ink2); font-weight:650}
.pl-jp-done .pl-jp-nm{color:var(--pl-ink2); text-decoration:line-through;
    text-decoration-color:rgba(137,135,129,.55)}
.pl-jp-ing{margin-left:22px}
.pl-jp-status{display:flex; align-items:center; gap:6px; flex-wrap:wrap; flex:1; font-size:12.5px; color:var(--pl-ink2); font-weight:600}
.pl-jp-est{flex:none; font-size:12px; color:var(--pl-ink2); font-weight:650}
.pl-jp-chev{flex:none; color:var(--pl-ink2); font-size:12px}

.pl-jp-slot{border:1px solid var(--pl-line); background:var(--pl-card2); border-radius:20px;
    padding:2px 10px; font-size:12px; font-weight:700; color:var(--pl-ink)}
.pl-jp-slot:hover{border-color:var(--plwc, #898781)}
.pl-jp-slot-done{color:var(--pl-ink2)}
.pl-jp-draft{font-style:normal; font-size:10px; font-weight:800; color:var(--pl-ink2);
    border:1px dashed var(--pl-ink3); border-radius:20px; padding:0 6px; margin-left:4px}

.pl-jp-pill{font-size:11px; font-weight:800; border-radius:20px; padding:2px 9px}
.pl-jp-pill-un{background:var(--pl-warn-soft, #fdf3d9); color:var(--pl-warn, #a86a00)}
.pl-jp-pill-done{background:var(--pl-good-soft, #e2f6e2); color:var(--pl-good, #0ca30c); border:0}
.pl-jp-pill-part{background:var(--pl-good-soft, #e2f6e2); color:var(--pl-good, #0ca30c); opacity:.75}

/* checklist-view tick target — big enough for shop-floor fingers */
.pl-jp-cb{width:18px; height:18px; flex:none; margin:0; cursor:pointer; accent-color:var(--pl-good, #0ca30c)}
.pl-jp-cb:disabled{cursor:wait; opacity:.5}
.pl-jp-tickbtn{cursor:pointer}

.pl-jp-grp{cursor:pointer}
.pl-jp-grp:hover{background:var(--pl-card2)}
</style>
